import { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
  LinearProgress,
  Box,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import StorageIcon from '@material-ui/icons/Storage';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { dbDumpPermission } from '../../permissions';

const useStyles = makeStyles(theme => ({
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  status: {
    marginTop: theme.spacing(2),
  },
}));

interface DatabaseEntry {
  pod: string;
  engine: string;
  database: string;
}

export const DatabaseDumpCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const environment = entity.metadata.annotations?.['fondative.io/environment'];
  const { allowed: canDump } = usePermission({ permission: dbDumpPermission });

  const [databases, setDatabases] = useState<DatabaseEntry[]>([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [loadingDbs, setLoadingDbs] = useState(false);
  const [dumping, setDumping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  // Fetch databases on mount
  useEffect(() => {
    if (!environment) return;

    let cancelled = false;
    setLoadingDbs(true);
    setError(null);

    fetchApi
      .fetch(
        `${backendBaseUrl}/api/db-dump/databases?environment=${environment}`,
      )
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            typeof body.error === 'string'
              ? body.error
              : body.error?.message || `HTTP ${res.status}`,
          );
        }
        return res.json();
      })
      .then((data: DatabaseEntry[]) => {
        if (!cancelled) {
          setDatabases(data);
          setLoadingDbs(false);
          if (data.length === 1) {
            setSelectedDb(data[0].database);
          }
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message);
          setLoadingDbs(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [environment, backendBaseUrl, fetchApi]);

  const handleDump = useCallback(async () => {
    if (!environment) return;

    setDumping(true);
    setError(null);
    setDownloadUrl(null);
    setSummary(null);

    try {
      const res = await fetchApi.fetch(
        `${backendBaseUrl}/api/db-dump/dump`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            environment,
            database: selectedDb || undefined,
          }),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          typeof body.error === 'string'
            ? body.error
            : body.error?.message || `HTTP ${res.status}`,
        );
      }

      const result = await res.json();
      if (result.downloadUrl) {
        setDownloadUrl(result.downloadUrl);
      }
      if (result.summary) {
        setSummary(result.summary);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDumping(false);
    }
  }, [environment, selectedDb, backendBaseUrl, fetchApi]);

  if (!environment) {
    return (
      <Card>
        <CardHeader
          title="Dump DB Anonymisé"
          avatar={<StorageIcon />}
        />
        <CardContent>
          <Typography color="textSecondary">
            Annotation <code>fondative.io/environment</code> manquante sur cette ressource.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Dump DB Anonymisé"
        subheader={`Environnement : ${environment}`}
        avatar={<StorageIcon />}
      />
      <CardContent>
        <FormControl className={classes.formControl} fullWidth>
          <InputLabel>Base de données</InputLabel>
          <Select
            value={selectedDb}
            onChange={e => setSelectedDb(e.target.value as string)}
            disabled={dumping || loadingDbs}
          >
            <MenuItem value="">
              <em>Toutes les bases (auto)</em>
            </MenuItem>
            {databases.map(db => (
              <MenuItem
                key={`${db.pod}-${db.database}`}
                value={db.database}
              >
                {db.database}{' '}
                <Chip
                  label={db.engine}
                  size="small"
                  style={{ marginLeft: 8 }}
                />
              </MenuItem>
            ))}
          </Select>
          {loadingDbs && <LinearProgress style={{ marginTop: 4 }} />}
        </FormControl>

        {canDump ? (
          <div className={classes.actions}>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                dumping ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <GetAppIcon />
                )
              }
              onClick={handleDump}
              disabled={dumping || loadingDbs}
            >
              {dumping ? 'Dump en cours...' : 'Lancer le dump'}
            </Button>
          </div>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Vous n'avez pas les droits pour effectuer un dump.
          </Typography>
        )}

        {error && (
          <Box className={classes.status}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {summary && (
          <Box className={classes.status}>
            <Typography variant="body2">{summary}</Typography>
          </Box>
        )}

        {downloadUrl && (
          <Box className={classes.status}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              href={downloadUrl}
            >
              Télécharger le dump
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
